// @flow

import * as React from 'react';

import type { FlyoutTriggerAttributes } from 'reshaped';

import { useEntityPermissions } from '@gandi/acl';
import { Button, type ButtonProps } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectMissingOrgInfo } from 'src/modules/organization/selectors';
import { ORGA_WRITE } from 'src/modules/permissions';

import { ButtonTooltip } from '../../ButtonTooltip';
import type { Props as ModalProps } from '../../hooks/useModalType';

type CustomButtonProps = {
  ...ButtonProps,
  attributes?: FlyoutTriggerAttributes,
};

type Props = {
  value: string,
  onClick: (value: string) => Promise<void> | void,
  permissions?: Array<string>,
  openModal: ModalProps['openModal'],
  loading?: boolean,
  disabled?: boolean,
};

function CustomButton({ attributes, ...props }: CustomButtonProps) {
  const { __ } = useTranslator();

  return (
    <Button
      fullWidth
      color="primary"
      size="large"
      // $FlowFixMe[incompatible-type]
      // $FlowFixMe[incompatible-type-arg]
      attributes={{ style: { flex: 1 }, ...attributes }}
      type="submit"
      {...props}
    >
      {__('Select')}
    </Button>
  );
}

function TooltipContent() {
  const { __ } = useTranslator();
  return (
    <>
      <strong>{__('Incomplete address')}</strong>{' '}
      {__('Please contact the owner to complete the missing information.')}
    </>
  );
}
/**
 * Submit button for organization selection.
 *
 * Handles organization information validation logic before selection:
 * - If information is complete: directly executes the onClick action
 * - If fields are missing and user has permissions: opens the edit modal
 * - If fields are missing without permissions: displays a tooltip and disables the button
 *
 * Note: The use case without feature flag is also handled at the "Continue" button level
 * in account choice to ensure user journey consistency.
 */
export function SubmitButton({
  value,
  onClick,
  openModal,
  permissions,
  loading,
  disabled,
}: Props): React.MixedElement {
  const [selecting, setSelecting] = React.useState(false);

  const { hasPermissions } = useEntityPermissions(value);
  const hasPermission = hasPermissions(permissions || [ORGA_WRITE]);

  const missingFields = useAppSelector(state =>
    selectMissingOrgInfo(state, { orgId: value }),
  );

  const hasMissingFields = !!missingFields?.length;

  const showMissingPermissionsTooltip = !hasPermission && hasMissingFields;
  const shouldOpenEditModal = hasPermission && hasMissingFields;
  const shouldExecuteOnClick =
    !showMissingPermissionsTooltip && !shouldOpenEditModal;

  async function handleSelect(evt: SyntheticEvent<>) {
    evt.preventDefault();
    if (shouldOpenEditModal) {
      openModal({ type: 'editAccountModal', orgId: value });
    } else if (shouldExecuteOnClick) {
      setSelecting(true);
      await onClick(value);
      setSelecting(false);
    }
  }

  return (
    <ButtonTooltip
      as={CustomButton}
      disabled={disabled || showMissingPermissionsTooltip}
      tooltipText={showMissingPermissionsTooltip && <TooltipContent />}
      onClick={handleSelect}
      loading={selecting || loading}
    />
  );
}
